import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom";
import LoginRedirect from "./auth/LoginRedirect";
import DemandData from "./routes/demanddata/DemandData";
import ErrorElement from "./routes/ErrorElement";
import OperationalTabs from "./routes/OperationalTabs";
import OrgEdit from "./routes/org/OrgEdit";
import OrgList from "./routes/org/OrgList";
import Costs from "./routes/Costs";
import DataConfig from "./routes/DataConfig";
import LeadImport from "./routes/leads/LeadImport";
import LeadsPage from "./routes/leads/LeadsPage";
import PortfolioReviewPage from "./routes/leads/PortfolioReviewPage";
import LeadSearchHistoryPage from "./routes/leads/LeadSearchHistoryPage";
import LeadSearchPage from "./routes/leads/LeadSearchPage";
import LeadSearchStatusPage from "./routes/leads/LeadSearchStatusPage";
import SiteEdit from "./routes/site/SiteEdit";
import SiteFilesReportsTabs from "./routes/site/SiteFilesReportsTabs";
import SiteHealthTabs from "./routes/site/SiteHealthTabs";
import SiteList from "./routes/site/SiteList";
import SiteNew from "./routes/site/SiteNew";
import SitePerformanceTabs from "./routes/site/SitePerformanceTabs";
import SitePvReport from "./routes/site/SitePvReportPage";
import SiteScenarios from "./routes/site/SiteScenarios";
import SiteSdm from "./routes/site/SiteSdm";
import Template from "./routes/Template";

const SiteRedirect = ({ to }: { to: string }) => {
  const { siteId } = useParams();
  return <Navigate to={`/site/${siteId}/${to}`} replace />;
};

const LeadIdRedirect = () => {
  const { leadId } = useParams();
  return <Navigate to={`/leads?selected=${leadId ?? ""}`} replace />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Template />,
    children: [
      {
        errorElement: <ErrorElement />,
        children: [
          // Default route
          {
            index: true,
            element: <Navigate to="/operational/deployment-stats" replace />,
          },

          // Operational
          { path: "/operational", element: <Navigate to="deployment-stats" replace /> },
          { path: "/operational/:tab", element: <OperationalTabs /> },

          // Site
          {
            path: "/site",
            element: <SiteList />,
          },
          {
            path: "/site/new",
            element: <SiteNew />,
            children: [
              {
                path: "/site/new/lat/:lat/lon/:lon/bkey/:bkey",
                element: <SiteNew />,
              },
            ],
          },
          {
            path: "/site/:siteId/edit",
            element: <SiteEdit />,
          },
          {
            path: "/site/:siteId/sdm",
            element: <SiteSdm />,
          },
          {
            path: "/site/:siteId/scenarios",
            element: <SiteScenarios />,
          },
          { path: "/site/:siteId/filesreports", element: <Navigate to="files" replace /> },
          { path: "/site/:siteId/filesreports/:tab", element: <SiteFilesReportsTabs /> },
          { path: "/site/:siteId/perf", element: <Navigate to="forecast" replace /> },
          { path: "/site/:siteId/perf/:tab", element: <SitePerformanceTabs /> },
          { path: "/site/:siteId/health", element: <Navigate to="status" replace /> },
          { path: "/site/:siteId/health/:tab", element: <SiteHealthTabs /> },

          // Organisation
          {
            path: "/organisation",
            element: <OrgList />,
          },
          {
            path: "/organisation/:orgId/edit",
            element: <OrgEdit />,
          },

          // Platform-wide AI / metered-API spend, every surface in one place.
          {
            path: "/costs",
            element: <Costs />,
          },

          // Leads
          {
            path: "/leads",
            element: <LeadsPage />,
          },
          {
            path: "/leads/search",
            element: <LeadSearchPage mode="new" />,
          },
          {
            path: "/leads/search/history",
            element: <LeadSearchHistoryPage />,
          },
          {
            path: "/leads/search/status",
            element: <LeadSearchStatusPage />,
          },
          {
            path: "/leads/import",
            element: <LeadImport />,
          },
          {
            path: "/leads/portfolio/:tag",
            element: <PortfolioReviewPage />,
          },
          // Legacy lead detail / reviewer / batches URLs all collapse
          // into the inline-decision /leads page.
          {
            path: "/leads/review",
            element: <Navigate to="/leads" replace />,
          },
          {
            path: "/leads/batches",
            element: <Navigate to="/leads" replace />,
          },
          {
            path: "/leads/batches/:batchId",
            element: <Navigate to="/leads" replace />,
          },
          {
            path: "/leads/review/:leadId",
            element: <LeadIdRedirect />,
          },
          {
            path: "/leads/:leadId",
            element: <LeadIdRedirect />,
          },

          // Top-level pages
          {
            path: "/data-config",
            element: <DataConfig />,
          },
          {
            path: "/demand",
            element: <DemandData />,
          },
          {
            path: "/login-redirect",
            element: <LoginRedirect />,
          },

          // --- Backwards-compatibility redirects ---
          // /site/:siteId/internal → content moved to health/setup and filesreports/reports
          { path: "/site/:siteId/internal", element: <SiteRedirect to="health/setup" /> },
          // /site/:siteId/files → moved to /filesreports/files
          { path: "/site/:siteId/files", element: <SiteRedirect to="filesreports/files" /> },
          // /site/:siteId/readings → moved under /perf
          { path: "/site/:siteId/readings", element: <SiteRedirect to="perf/readings" /> },
          // /reports → renamed to /data-config
          { path: "/reports", element: <Navigate to="/data-config" replace /> },
        ],
      },
    ],
  },
  // Outside the Template layout — renders without the standard top nav
  {
    path: "/site/:siteId/pvreport",
    element: <SitePvReport />,
  },
]);

const AppRoutes = () => {
  return <RouterProvider router={router} />;
};

export default AppRoutes;
